import React, { useCallback, useRef } from "react";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import AddPageBlock from "organisms/page-blocks/add-page-block/add-page-block";
import {
    DEFAULT_SORTABLE_DRAG_AND_DROP_SCOPE_NAME,
    SharedSortableDragAndDropList,
    SortableDragAndDropScopeConfiguration,
} from "organisms/sortable-drap-and-drop/shared-sortable-drag-and-drop-list";
import usePageBlocks from "utilities/hooks/domain/notebooks/pages/use-page-blocks";
import { ShowOnlyIfReadonly } from "utilities/contexts/readonly/show-only-if-readonly";
import { HideIfReadonly } from "utilities/contexts/readonly/hide-if-readonly";
import { ReadonlyPageBlock } from "organisms/page-blocks/readonly-page-block/readonly-page-block";
import useOutsideClick from "utilities/hooks/use-outside-click";
import { PageBlock } from "./page-block";
import { NotebookReferencePanelRefAtom } from "utilities/atoms/notebook-reference-panel-ref-atom";
import { useAtomValue } from "jotai";
import useDragAndDropSection from "organisms/drag-and-drop/use-drag-and-drop-section";
import { PageBlockDraggable } from "organisms/page-blocks/page-block-draggable/page-block-draggable";
import { closestCenter, CollisionDetection } from "@dnd-kit/core";
import PageBlockAddMenu from "organisms/page-blocks/page-block-list/page-block-add-menu";
import { IconSizes } from "atoms/constants/icon-sizes";
import { ChecklistItemNoteModalRefAtom } from "organisms/checklist-item-notes/checklist-item-note-modal";
import Loader from "atoms/loaders/loader";
import useKeyPress from "utilities/hooks/use-key-press";
import { BlockRecord } from "models/view-models/block-record";
import LockedForEditingWrapper from "organisms/page-blocks/locked-for-editing/locked-for-editing-wrapper";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { usePageEditSessionContext } from "utilities/contexts/page-edit-session/page-edit-session-context-provider";
import useCurrentPageEditSession from "pages/notebooks/use-current-page-edit-session";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization-utils";
import useFeatureFlags from "utilities/hooks/use-feature-flags";

interface PageBlockListProps {
    onAnyBlockInListUpdated: () => Promise<void>;
    isInEditMode: boolean;
    onEditSessionTimeout: () => void;
    onRequestEditMode?: () => void;
}

const CSS_CLASS_NAME = "c-page-block-list";
export const PAGE_BLOCKS_DRAG_AND_DROP_SCOPE = `${DEFAULT_SORTABLE_DRAG_AND_DROP_SCOPE_NAME}-page-blocks`;

const PageBlockList = (props: PageBlockListProps) => {
    const {
        onAnyBlockInListUpdated,
        isInEditMode,
        onEditSessionTimeout,
        onRequestEditMode,
    } = props;
    const handleAnyBlockInListUpdated = useCallback(async (): Promise<void> => {
        await onAnyBlockInListUpdated();
    }, [onAnyBlockInListUpdated]);
    const { currentIdentity } = useCurrentIdentity();
    const listRef = useRef<HTMLElement>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const notebookPanelRef = useAtomValue(NotebookReferencePanelRefAtom);
    const checklistItemNoteModalRef = useAtomValue(
        ChecklistItemNoteModalRefAtom
    );
    const { allowNotebookCollaboration } = useFeatureFlags();

    const {
        activeBlock,
        deleteBlock: onDelete,
        draggingBlockId,
        hasNoBlocks,
        loading,
        moveBlocks: onMove,
        moveBlockToBottom: onMoveDown,
        moveBlockToTop: onMoveUp,
        onActivateBlock: activateBlock,
        onDeactivateBlock: deactivateBlock,
        onDragEnd,
        onDragStart,
        populatedBlocks,
        sortedBlocks,
        startNewBlock,
        startNewBlockAtEnd,
        updateBlockInList,
        fetchingBlocks,
        observerRef,
        refreshBlocksInListById,
    } = usePageBlocks({
        onAnyBlockInListUpdated: handleAnyBlockInListUpdated,
    });

    const {
        currentUserActiveSession,
        getSessionForBlock,
        startEditSession,
        updateEditSession,
        refreshSessionList,
    } = usePageEditSessionContext({
        handleBlocksChangedByAnotherUser: refreshBlocksInListById,
    });

    useDragAndDropSection();

    const handleEditSessionUpdate = useCallback(
        async (blockId?: number): Promise<boolean> => {
            if (!isInEditMode) {
                return false;
            }

            if (!currentUserActiveSession) {
                return await startEditSession(blockId!);
            } else {
                return await updateEditSession(
                    currentUserActiveSession.id!,
                    blockId
                );
            }
        },
        [
            currentUserActiveSession,
            isInEditMode,
            startEditSession,
            updateEditSession,
        ]
    );

    const handleBlockSaved = useCallback(
        (updatedBlock: BlockRecord): void => {
            updateBlockInList(updatedBlock);
            handleAnyBlockInListUpdated();
            handleEditSessionUpdate(updatedBlock.id);
        },
        [
            updateBlockInList,
            handleAnyBlockInListUpdated,
            handleEditSessionUpdate,
        ]
    );

    const handleActivateBlock = useCallback(
        async (block: BlockRecord) => {
            if (!allowNotebookCollaboration) {
                activateBlock(block);
                return;
            }
            try {
                const canEdit = await handleEditSessionUpdate(block.id);
                if (canEdit) {
                    activateBlock(block);
                } else {
                    refreshSessionList();
                }
            } catch (error) {
                ToastManager.error(
                    t("errors-actionResource", {
                        action: t("updating"),
                        resource: t("block"),
                    })
                );
                console.error(error);
            }
        },
        [
            activateBlock,
            allowNotebookCollaboration,
            handleEditSessionUpdate,
            refreshSessionList,
        ]
    );

    const handleDeactivateBlock = async (blockId?: number) => {
        if (!blockId) return;
        const sessionId = getSessionForBlock(blockId)?.id;
        if (sessionId) {
            await updateEditSession(sessionId);
        }
        deactivateBlock();
    };

    const keepOpenOnClickElements = [
        listRef,
        ...(notebookPanelRef ? [notebookPanelRef] : []),
        ...(checklistItemNoteModalRef ? [checklistItemNoteModalRef] : []),
    ];
    useOutsideClick(
        keepOpenOnClickElements,
        handleDeactivateBlock,
        "mousedown"
    );

    useCurrentPageEditSession({
        isInActiveEditSession: !!currentUserActiveSession,
        onSessionEnd: onEditSessionTimeout,
    });

    useKeyPress("Escape", handleDeactivateBlock);

    const isFirst = (index: number): boolean => index === 0;
    const isLast = (index: number): boolean =>
        index === sortedBlocks.length - 1;

    return (
        <section className={CSS_CLASS_NAME} ref={listRef}>
            {loading ? (
                <div className={`${CSS_CLASS_NAME}__loaderWrapper`}>
                    <Loader />
                </div>
            ) : (
                <>
                    <ShowOnlyIfReadonly>
                        {populatedBlocks.map((block) => (
                            <ReadonlyPageBlock
                                block={block}
                                key={block.id}
                                onRequestEditMode={onRequestEditMode}
                            />
                        ))}
                    </ShowOnlyIfReadonly>
                    <HideIfReadonly>
                        <SharedSortableDragAndDropList
                            dragAndDropScopeName={
                                PAGE_BLOCKS_DRAG_AND_DROP_SCOPE
                            }
                            dragAndDropScopeConfiguration={
                                dragAndDropScopeConfiguration
                            }
                            items={sortedBlocks}
                            onDragEnd={onDragEnd}
                            onSortChange={onMove}
                            onDragStart={onDragStart}
                            strategy={verticalListSortingStrategy}>
                            {sortedBlocks.map((block, index) => {
                                const blockEditSession = getSessionForBlock(
                                    block.id!
                                );
                                return (
                                    <LockedForEditingWrapper
                                        key={index}
                                        isLocked={
                                            isInEditMode &&
                                            blockEditSession?.userId !==
                                                currentIdentity?.userId() &&
                                            blockEditSession?.blockId ===
                                                block.id
                                        }
                                        firstName={
                                            blockEditSession?.user?.firstName
                                        }
                                        lastName={
                                            blockEditSession?.user?.lastName
                                        }>
                                        <PageBlock
                                            activateBlock={handleActivateBlock}
                                            activeBlock={activeBlock}
                                            block={block}
                                            draggingBlockId={draggingBlockId}
                                            isFirst={isFirst(index)}
                                            isLast={isLast(index)}
                                            key={block.id ?? index}
                                            onBlockModified={updateBlockInList}
                                            onBlockSaved={handleBlockSaved}
                                            onDelete={onDelete}
                                            onMoveDown={onMoveDown}
                                            onMoveUp={onMoveUp}
                                            startNewBlock={startNewBlock}
                                            onDeactivateBlock={() =>
                                                handleDeactivateBlock(block.id)
                                            }
                                        />
                                    </LockedForEditingWrapper>
                                );
                            })}
                        </SharedSortableDragAndDropList>
                        {hasNoBlocks && !activeBlock && (
                            <div className={`${CSS_CLASS_NAME}__addContent`}>
                                <AddPageBlock onAddContent={startNewBlock} />
                            </div>
                        )}
                        <div
                            ref={(node) => {
                                sentinelRef.current = node;
                                observerRef(node);
                            }}
                            className={`${CSS_CLASS_NAME}__sentinel`}
                        />
                        {fetchingBlocks && (
                            <div className={`${CSS_CLASS_NAME}__bottomLoader`}>
                                <Loader />
                            </div>
                        )}
                        <PageBlockAddMenu
                            showMenuOnLeft
                            size={IconSizes.Large}
                            cssClassName={`${CSS_CLASS_NAME}__addButton`}
                            onAddBlockBelow={startNewBlockAtEnd}
                        />
                    </HideIfReadonly>
                </>
            )}
        </section>
    );
};

export default PageBlockList;

const pageBlockCollisionDetection: CollisionDetection = (args) => {
    const topOffset = 10;
    const pointerCenteredCollisionRect = {
        ...args.collisionRect,
        left: args.pointerCoordinates?.x ?? args.collisionRect.left,
        top: (args.pointerCoordinates?.y ?? args.collisionRect.top) + topOffset,
    };

    return closestCenter({
        ...args,
        collisionRect: pointerCenteredCollisionRect,
    });
};

const dragAndDropScopeConfiguration: SortableDragAndDropScopeConfiguration = {
    collisionDetection: pageBlockCollisionDetection,
    contextModifiers: [],
    draggableComponent: PageBlockDraggable,
};
